import classNames from 'classnames';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useState } from 'react';

import { getAffiliateInfo } from '@/api/mkt';
import { WeeeIcon } from '@/components/common/Icon';
import useHomepage from '@/store/home';
import ACTIONS from '@/store/home/actions';
import { checkReferral, getLoginState } from '@/utils/business';
import logger from '@/utils/logger';
import ImpTracker from '@/components/common/ImpTracker';
import useImpressionT2 from '@/tracks/useImpressionT2';
import useClickActionT2 from '@/tracks/useClickActionT2';
import qs from 'qs';
import { XFilled } from '@weee-fe/kunlunicon';
import usePageMountEffect from '@/hooks/usePageMountEffect';
import { getCroppedImageUrl } from '../CroppedImage';

type RecommendBannerProps = {
  [key: string]: any;
  onChange?: (val: boolean) => void;
  className?: string;
  trackParams?: {
    co?: ObjectType;
    ctx?: ObjectType;
  };
  extraClasses?: string;
};

export default function RecommendBanner(props: RecommendBannerProps) {
  const router = useRouter();
  const { page, className, trackParams, extraClasses } = props;
  const [bannerStatus, setBannerStatus] = useState<boolean>(false);
  const [bannerData, setBannerData] = useState(null);
  const { query } = router;
  const { t, lang } = useTranslation('common');
  const isLogin = getLoginState();
  const { dispatch } = useHomepage();
  const { bannerImpression } = useImpressionT2();
  const { bannerClick } = useClickActionT2();
  const isNew = bannerData?.coupon_amount !== 0;
  const messageType = isLogin ? (isNew ? 1 : 2) : 3;

  const init = async () => {
    const isRefereal = checkReferral();
    const referral_id = +qs.parse(location.search.substring(1))?.referral_id || '';
    if (referral_id && !isRefereal) {
      try {
        const res = await getAffiliateInfo({
          referral_id: referral_id
        });

        if (res.result === true) {
          setBannerData(res?.object);
          setBannerStatus(true);
          page === 'homePage' &&
            dispatch({
              type: ACTIONS.RECOMMEND_BANNER_VISIBLE,
              payload: true
            });
        } else {
          page === 'homePage' &&
            dispatch({
              type: ACTIONS.RECOMMEND_BANNER_VISIBLE,
              payload: false
            });
        }
      } catch (error) {
        logger.error({
          page: window.location.href,
          method: 'getAffiliateInfo',
          error: error
        });
      }
    }
  };

  const closeBanner = useCallback(() => {
    bannerClick({
      mod_nm: 'affiliate_banner',
      mod_pos: 0,
      ...trackParams,
      co: {
        target_nm: messageType,
        target_pos: null,
        target_type: 'banner_line',
        click_type: 'view',
        ...trackParams?.co
      }
    });
    setBannerStatus(false);
  }, []);

  const toSignup = useCallback(() => {
    const referral_id = router.query.referral_id;
    const url = encodeURIComponent(window.location.href);
    bannerClick({
      mod_nm: 'affiliate_banner',
      mod_pos: 0,
      ...trackParams,
      co: {
        target_nm: messageType,
        target_pos: null,
        target_type: 'banner_line',
        click_type: 'view',
        ...trackParams?.co
      }
    });
    window.location.href = '/account/signup?referral_id=' + referral_id + '&redirect_url=' + url;
  }, [query.referral_id]);

  usePageMountEffect(() => {
    init();
  }, []);

  useEffect(() => {
    props.onChange?.(true);
  }, [bannerStatus]);

  return (
    bannerStatus && (
      <ImpTracker
        impTrackingT2={() => {
          bannerImpression({
            mod_nm: 'affiliate_banner',
            mod_pos: 0,
            ...trackParams,
            co: {
              banner_id: null,
              banner_key: null,
              banner_pos: 0,
              banner_type: 'banner_line',
              url: window.location.href,
              message_type: messageType,
              ...trackParams?.co
            }
          });
        }}>
        <div
          data-testid="recommend-banner-container"
          className={classNames('bg-highlight-bg box-border', className, extraClasses)}>
          <div className="px-4 py-2 relative">
            <XFilled onClick={closeBanner} className="absolute top-1 right-3 text-xl m-1 z-[1]" />
            <div className="flex justify-between">
              {!!bannerData?.headImgUrl && (
                <img
                  src={getCroppedImageUrl(bannerData?.headImgUrl, {
                    renderWidth: 80
                  })}
                  alt="head_img"
                  className="shrink-0 mr-3 w-10 h-10 rounded-full overflow-hidden bg-white"
                />
              )}
              <div className="flex-1">
                <p className="enki-body-base-medium pr-5">{bannerData?.alias}</p>
                <p className="enki-body-2xs line-clamp-1 pr-5">
                  {isLogin && !isNew ? t('common:recommend_banner_user') : t('common:recommend_banner_user_new')}
                </p>
                <div className="flex items-center justify-between">
                  <p className="flex-1 mt-1 enki-body-base-medium pr-3">
                    {isLogin ? (
                      isNew ? (
                        <Trans
                          i18nKey={'common:recommend_banner_enjoy'}
                          values={{
                            number: Number(bannerData?.coupon_amount)
                          }}
                          components={{
                            span: <span className="text-pricing-txt font-semibold"></span>
                          }}
                        />
                      ) : (
                        bannerData?.follow_buy_num !== 0 && (
                          <Trans
                            i18nKey={'common:recommend_banner_count'}
                            values={{
                              number: Number(bannerData.follow_buy_num)
                            }}
                          />
                        )
                      )
                    ) : (
                      <Trans
                        i18nKey={'common:recommend_banner_welcome'}
                        values={{
                          number: Number(bannerData?.refer_coupon_amount)
                        }}
                        components={{
                          span: <span className="text-pricing-txt font-semibold"></span>
                        }}
                      />
                    )}
                  </p>

                  {!isLogin && (
                    <div
                      className="enki-body-base-medium text-link-base-1 bg-surface-100-bg px-2.5 py-1 rounded-full border border-solid border-primary-2"
                      onClick={toSignup}>
                      {t('recommend_banner_login')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </ImpTracker>
    )
  );
}
